/**
 * Anim — layered procedural animation for the soldier rig.
 *
 * LAYERS, evaluated in this order every frame:
 *
 *   0  base locomotion   FK spine/hips: lean, sway, bob, breathing, crouch, arm swing
 *   1  upper-body aim    spine twist + pitch toward the aim target, per-bone limited
 *   2  action modulators reload / vault / throw / cover / peek — these move the WEAPON
 *                        anchor and the hand IK targets rather than baking arm curves,
 *                        so they compose with whatever locomotion is underneath
 *   3  additive          recoil and per-region hit flinch as critically-damped springs,
 *                        multiplied onto the base pose so they never pop
 *   4  IK                arms two-bone onto the weapon, legs two-bone onto the planted
 *                        foot targets, hips lowered until both legs can reach
 *   5  look-at           neck + head toward the look target with joint limits and a
 *                        first-order lead-lag so the head arrives after the body
 *
 * GAIT. Phase is advanced by DISTANCE TRAVELLED, not by time, so the cadence can never
 * disagree with the ground speed. Each foot records a world-space plant when it lands
 * and the IK target is that exact point for the whole stance phase, so foot sliding is
 * impossible by construction rather than merely tuned away. Plants are raycast onto the
 * real ground through `ctx.physics.raycast`; the hips then drop until the longer leg
 * can reach, which is all that stairs and slopes need.
 *
 * owner: characters
 */
import * as THREE from 'three';
import { clamp, lerp, smooth } from './Build.js';
import { DYNAMIC_BONES } from './Rig.js';

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _bx = new THREE.Vector3();
const _by = new THREE.Vector3();
const _bz = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();
const _qc = new THREE.Quaternion();
const _qd = new THREE.Quaternion();
const _e = new THREE.Euler();
const _ik1 = new THREE.Vector3();
const _ik2 = new THREE.Vector3();
const _ik3 = new THREE.Vector3();
const DOWN = new THREE.Vector3(0, -1, 0);
const UP = new THREE.Vector3(0, 1, 0);
const RIGHT = new THREE.Vector3(1, 0, 0);
const FWD = new THREE.Vector3(0, 0, 1);

/** Orthonormal frame with +Y along `dir`, +Z biased toward `ref`. Identity for (Y, Z). */
export function basisQuat(dir, ref, out) {
  _by.copy(dir).normalize();
  _bx.crossVectors(_by, ref);
  if (_bx.lengthSq() < 1e-10) _bx.crossVectors(_by, Math.abs(_by.z) < 0.9 ? FWD : RIGHT);
  if (_bx.lengthSq() < 1e-10) _bx.set(1, 0, 0);
  _bx.normalize();
  _bz.crossVectors(_bx, _by);
  _m.makeBasis(_bx, _by, _bz);
  return out.setFromRotationMatrix(_m);
}

/** Orthonormal frame with +Z along `dir`, +Y biased toward `up`. Identity for (Z, Y). */
export function frameQuatZ(dir, up, out) {
  _bz.copy(dir).normalize();
  _bx.crossVectors(up, _bz);
  if (_bx.lengthSq() < 1e-10) _bx.crossVectors(Math.abs(_bz.y) < 0.9 ? UP : FWD, _bz);
  if (_bx.lengthSq() < 1e-10) _bx.set(1, 0, 0);
  _bx.normalize();
  _by.crossVectors(_bz, _bx);
  _m.makeBasis(_bx, _by, _bz);
  return out.setFromRotationMatrix(_m);
}

/**
 * Analytic two-bone IK. Writes the mid-joint world position into `outMid` and the
 * reach-clamped end position into `outEnd`.
 */
export function twoBoneIK(root, target, lenA, lenB, pole, outMid, outEnd) {
  // dedicated temps: callers routinely pass the shared module scratch as `root`
  _ik1.copy(target).sub(root);
  let d = _ik1.length();
  if (d < 1e-6) { _ik1.set(0, -1, 0); d = 1e-6; }
  const dir = _ik2.copy(_ik1).divideScalar(d);
  const maxD = (lenA + lenB) * 0.997;
  const minD = Math.abs(lenA - lenB) * 1.02 + 1e-4;
  d = clamp(d, minD, maxD);
  const a = (lenA * lenA - lenB * lenB + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, lenA * lenA - a * a));
  _ik3.copy(pole).addScaledVector(dir, -pole.dot(dir));
  if (_ik3.lengthSq() < 1e-10) {
    _ik3.copy(FWD).addScaledVector(dir, -dir.z);
    if (_ik3.lengthSq() < 1e-10) _ik3.set(1, 0, 0);
  }
  _ik3.normalize();
  const rx = root.x, ry = root.y, rz = root.z;
  outMid.set(rx + dir.x * a + _ik3.x * h, ry + dir.y * a + _ik3.y * h, rz + dir.z * a + _ik3.z * h);
  outEnd.set(rx + dir.x * d, ry + dir.y * d, rz + dir.z * d);
  return d;
}

/** Critically-damped spring toward 0 — the engine behind every additive layer. */
export class Spring {
  constructor(freq = 12, damp = 1) { this.v = 0; this.x = 0; this.f = freq; this.d = damp; }
  kick(a) { this.v += a; }
  step(dt) {
    const w = this.f;
    const steps = dt > 1 / 90 ? 2 : 1;
    const h = dt / steps;
    for (let i = 0; i < steps; i++) {
      this.v += (-w * w * this.x - 2 * this.d * w * this.v) * h;
      this.x += this.v * h;
    }
    return this.x;
  }
}

/**
 * THE STRIDE MODEL — read this before touching a number in the table.
 *
 * A 1.80 m soldier's hip joint sits 0.950 m up and his leg is 0.853 m long, so with the
 * ankle 0.098 m above the sole the hip is only 0.003 m short of straight. The horizontal
 * room at the ankle without dropping the hips is therefore
 *
 *     sqrt(reach^2 - (hipY - ankleY)^2)  ~=  0.13 m
 *
 * and about 0.50 m backwards once the foot has rolled onto its toe. That is a hard
 * geometric budget of roughly **0.63 m of ground travel per stance phase**, and it is the
 * constraint the previous model ignored. It authored `stride` and `duty` independently,
 * which asked the run's locked foot to cover 2 * 1.10 * 0.36 = 0.79 m, planted the foot
 * 0.35 m ahead of the hip, and paid the 0.16 m difference by dropping the hips 7.5 cm at
 * every heel strike. That is the permanent lunge in the old char_locomotion frame.
 *
 * Three things fix it and they only work together:
 *
 *   1. `ahead` is now a fraction of the *reachable* front budget, not of the stride, so
 *      the foot plants under or just ahead of the hip whatever the speed.
 *   2. `duty` is clamped at runtime to what the leg can actually cover (`_reach()`), so
 *      the foot is released the moment it would otherwise have to drag the hips down.
 *   3. The heel-off half of stance pivots the foot about the TOE rather than the ankle
 *      (see `_legIK`). The ankle rises AND travels forward, which is where the back half
 *      of the reach comes from on a real leg. The old `heel` term only added the rise.
 *
 * `stride` is the STEP LENGTH in metres: one full phase cycle covers `2 * stride`, so
 * cadence = speed / (2 * stride) cycles per second. Real running is 170-190 steps/min;
 * the old run table was 256.
 *
 * `toeOff` is the peak toe-down roll in radians at the end of stance. It sets both the
 * pose and the reach budget, so the two can never disagree.
 */
const GAITS = {
  idle: { stride: 0.00, duty: 1.00, lift: 0.000, bob: 0.008, lean: 0.00, armSwing: 0.05, ahead: 0.10, toeOff: 0.04, sink: 0.000 },
  walk: { stride: 0.62, duty: 0.58, lift: 0.070, bob: 0.024, lean: 0.05, armSwing: 0.28, ahead: 0.85, toeOff: 0.44, sink: 0.022 },
  run: { stride: 1.22, duty: 0.34, lift: 0.150, bob: 0.048, lean: 0.18, armSwing: 0.55, ahead: 0.85, toeOff: 0.62, sink: 0.038 },
  sprint: { stride: 1.60, duty: 0.28, lift: 0.215, bob: 0.066, lean: 0.32, armSwing: 0.80, ahead: 0.90, toeOff: 0.70, sink: 0.048 },
  crouch: { stride: 0.54, duty: 0.66, lift: 0.048, bob: 0.014, lean: 0.10, armSwing: 0.16, ahead: 0.55, toeOff: 0.30, sink: 0.000 },
};

/**
 * `sink` above is the metres a *moving* soldier's hips sit below a standing one's. It is
 * not decoration: with a straight-legged 0.950 m hip the ankle has 7 cm of horizontal
 * room and no stride is possible at all. 3-5 cm of knee flex buys ~25 cm, which is what
 * a real runner's landing offset actually is. Because `_reach()` reads the same number,
 * the pose and the reach budget can never disagree.
 */

/** Ceiling on the plant offset as a fraction of the step length — a crouch-walker has
 * plenty of leg room but must not take a running man's stride. */
const AHEAD_OF_STRIDE = 0.42;

/**
 * The bind pose stands with the knees locked, which makes the two-bone solve degenerate.
 * Every stance sits this far lower so the knee always has a bend direction — and no
 * soldier stands with locked knees. Hoisted out of `_basePose` because the reach budget
 * has to agree with it.
 */
const SOFT_KNEE = 0.026;
/**
 * Metres of hip dip the stride is allowed to spend buying reach at toe-off. This is
 * paid by the `hipDrop` valve in `_legIK`, so it has to stay small enough to read as
 * a runner's natural vertical oscillation rather than as a crouch.
 */
const HIP_GIVE = 0.02;

const ACTION_DUR = {
  reload: 2.35, reloadTactical: 1.9, vault: 1.0, throw: 1.15, melee: 0.8, point: 1.4,
};

export class Animator {
  /** @param {object} rig @param {THREE.Object3D} root @param {object} opts */
  constructor(rig, root, opts = {}) {
    this.rig = rig;
    this.root = root;
    this.physics = opts.physics || null;
    this.gear = opts.gear || {};
    this.S = rig.scale;
    this.rng = opts.rng || (() => 0.5);
    this.time = opts.phase ?? 0;

    const b = rig.bind;
    this.thighLen = b.thighL.distanceTo(b.shinL);
    this.shinLen = b.shinL.distanceTo(b.footL);
    this.legLen = this.thighLen + this.shinLen;
    this.upperArmLen = b.upperArmL.distanceTo(b.forearmL);
    this.forearmLen = b.forearmL.distanceTo(b.handL);

    this.velocity = new THREE.Vector3();
    this.speed = 0;
    this.grounded = true;
    this.crouched = false;
    this.stance = 'stand';
    this.yaw = root.rotation.y;
    this.prevYaw = this.yaw;
    this.yawRate = 0;

    this.phase = opts.phase ?? 0;
    this.gaitName = 'idle';
    this.gaitBlend = { ...GAITS.idle };
    this.crouchAmt = 0;
    this.hipDrop = 0;
    this.dt = 1 / 60;

    /* ---- stride model, all derived in _gait() from the reach budget ---- */
    // ankle -> toe in the bind pose, in the foot's own (forward, up) plane
    this.toeFwd = rig.bind.toeL.z - rig.bind.footL.z;     // ~0.130 m
    this.toeDrop = rig.bind.footL.y - rig.bind.toeL.y;    // ~0.066 m
    this.ankleH = rig.bind.footL.y;                       // sole -> ankle
    this.heelBack = 0.075 * rig.scale;                    // ankle -> heel, backwards
    this.reachFront = 0.12 * rig.scale;
    this.reachBack = 0.45 * rig.scale;
    this.duty = 1;
    this.aheadM = 0;
    this.feet = { L: this._newFoot(-1, 0), R: this._newFoot(1, 0.5) };
    this.groundY = root.position.y;
    this.groundNormal = new THREE.Vector3(0, 1, 0);

    this.aimTarget = new THREE.Vector3();
    this.lookTarget = new THREE.Vector3();
    this.aimSmooth = new THREE.Vector3();
    this.lookSmooth = new THREE.Vector3();
    this.aimWeight = 1;
    this.hasAim = false;
    this.hasLook = false;
    this.headYaw = 0; this.headPitch = 0;
    this._aimInit = false;

    this.recoilPitch = new Spring(34, 0.42);
    this.recoilYaw = new Spring(30, 0.5);
    this.recoilBack = new Spring(28, 0.55);
    this.flinch = {};
    this.breath = opts.phase ? opts.phase * 6 : 0;

    this.measured = false;
    this.autoFace = false;
    this.speedHint = 0;
    this._lastPos = null;

    this.action = null;
    this.lean = 0;
    this.leanTarget = 0;
    this.postureBlend = 1;
    this.postureTarget = 1;

    this.ragdoll = null;
    this.driveTable = null;
    this.ragdollBlend = 0;
    this.dead = false;

    this.weaponQuat = new THREE.Quaternion();
    this.weaponPos = new THREE.Vector3();

    // persistent scratch — this runs for twelve characters at 60 Hz
    this._t = Array.from({ length: 10 }, () => new THREE.Vector3());
    this._rootQ = new THREE.Quaternion();
  }

  _newFoot(side, offset) {
    return {
      side, offset, planted: true, plantValid: false,
      plant: new THREE.Vector3(), normal: new THREE.Vector3(0, 1, 0),
      lift: new THREE.Vector3(), liftOff: new THREE.Vector3(), target: new THREE.Vector3(),
      swingTo: new THREE.Vector3(), swingValid: false,
      pendingNormal: new THREE.Vector3(0, 1, 0), pendingValid: false,
      plantYaw: 0, roll: 0, ground: 1, ankle: new THREE.Vector3(),
    };
  }

  /* ================================================================ *
   * input
   * ================================================================ */
  /**
   * Two accepted forms:
   *   setLocomotion({ velocity, grounded, crouched, stance })   — the contract form
   *   setLocomotion(speed, verticalSpeed, crouched)             — the AI team's form
   * The positional form also switches the rig into MEASURED mode: velocity and facing
   * are derived from the root's own motion, because a driver that only writes
   * `character.position` never tells us which way the body should point.
   */
  setLocomotion(a, b, c) {
    if (typeof a === 'number' || typeof a === 'boolean') {
      this.measured = true;
      this.autoFace = true;
      this.speedHint = typeof a === 'number' ? a : 0;
      this.crouched = !!c;
      this.grounded = true;
      return;
    }
    const s = a || {};
    if (s.velocity) { this.velocity.set(s.velocity.x, 0, s.velocity.z); this.measured = false; }
    this.speed = this.velocity.length();
    if (s.grounded !== undefined) this.grounded = !!s.grounded;
    if (s.crouched !== undefined) this.crouched = !!s.crouched;
    if (s.stance) { this.stance = s.stance; if (s.stance === 'crouch') this.crouched = true; }
    if (s.crouched === false && s.stance === undefined) this.crouched = false;
    void b;
  }

  setAimTarget(v) {
    if (!v) return;
    this.aimTarget.copy(v);
    if (!this._aimInit) { this.aimSmooth.copy(v); this.lookSmooth.copy(v); this._aimInit = true; }
    this.hasAim = true;
  }

  setLookTarget(v) {
    if (!v) return;
    this.lookTarget.copy(v);
    if (!this._aimInit) { this.lookSmooth.copy(v); this.aimSmooth.copy(v); this._aimInit = true; }
    this.hasLook = true;
  }

  play(name, opts = {}) {
    if (name === 'fire') return this.fire(opts);
    if (name === 'cover') { this.leanTarget = opts.side === 'left' ? -1 : 1; this.postureTarget = 0.3; return; }
    if (name === 'peek') { this.leanTarget = opts.side === 'left' ? -0.8 : 0.8; this.postureTarget = 1; return; }
    if (name === 'uncover' || name === 'idle') { this.leanTarget = 0; this.postureTarget = 1; return; }
    if (name === 'lowReady') { this.postureTarget = 0; return; }
    this.action = { name, t: 0, dur: opts.duration ?? ACTION_DUR[name] ?? 1, opts };
    return undefined;
  }

  fire(opts = {}) {
    const p = opts.power ?? 1;
    this.recoilPitch.kick(-6.4 * p);
    this.recoilYaw.kick((this.rng() - 0.5) * 3.4 * p);
    this.recoilBack.kick(-0.9 * p);
  }

  hit(region, impulse, point) {
    const mag = impulse ? Math.min(1.6, impulse.length() / 45) : 0.6;
    let f = this.flinch[region];
    if (!f) f = this.flinch[region] = { s: new Spring(region === 'head' ? 25 : 16, 0.32), dir: new THREE.Vector3(0, 0, -1) };
    f.s.kick(-9 * (0.35 + mag));
    if (impulse && impulse.lengthSq() > 1e-8) f.dir.copy(impulse).normalize();
    void point;
  }

  /* ================================================================ *
   * frame
   * ================================================================ */
  update(dt) {
    if (!(dt > 0)) return;
    this.dt = dt;
    this.time += dt;
    this._measure(dt);
    this.root.getWorldQuaternion(this._rootQ);

    const y = this.root.rotation.y;
    let dY = y - this.prevYaw;
    while (dY > Math.PI) dY -= Math.PI * 2;
    while (dY < -Math.PI) dY += Math.PI * 2;
    this.yawRate = dY / dt;
    this.prevYaw = y;
    this.yaw = y;

    if (this.dead && this.ragdoll) {
      this.ragdollBlend = Math.min(1, this.ragdollBlend + dt / 0.12);
      this._applyRagdoll();
      return;
    }

    this._springs(dt);
    this._gait(dt);
    this._basePose();
    this.rig.rootBone.updateMatrixWorld(true);

    this._spineAim(dt);
    this.rig.rootBone.updateMatrixWorld(true);

    this._legIK();
    this._armIK();
    this._lookAt(dt);
    this.rig.rootBone.updateMatrixWorld(true);
  }

  /**
   * MEASURED mode: derive velocity from the root's own displacement and turn the body
   * to face the aim (or the direction of travel) at a human turn rate. This is what
   * lets a driver that only writes `character.position` still get a soldier who strafes
   * with his weapon up instead of moon-walking sideways.
   */
  _measure(dt) {
    if (!this._lastPos) { this._lastPos = this.root.position.clone(); return; }
    _v1.copy(this.root.position).sub(this._lastPos).divideScalar(dt);
    this._lastPos.copy(this.root.position);
    if (this.measured) {
      _v1.y = 0;
      if (_v1.lengthSq() > 400) _v1.set(0, 0, 0);       // a teleport is not a run
      this.velocity.lerp(_v1, Math.min(1, dt * 12));
      const sp = this.velocity.length();
      if (this.speedHint > 0.05 && sp > 0.05) this.velocity.multiplyScalar(this.speedHint / sp);
      else if (this.speedHint > 0.05 && sp <= 0.05) {
        this.velocity.set(0, 0, this.speedHint).applyQuaternion(this.root.quaternion);
      }
      this.speed = this.velocity.length();
    }
    if (!this.autoFace) return;
    let want = null;
    if (this.hasAim) {
      _v2.copy(this.aimTarget).sub(this.root.position);
      if (_v2.x * _v2.x + _v2.z * _v2.z > 1e-4) want = Math.atan2(_v2.x, _v2.z);
    } else if (this.speed > 0.35) {
      want = Math.atan2(this.velocity.x, this.velocity.z);
    }
    if (want === null) return;
    let d = want - this.root.rotation.y;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    this.root.rotation.y += clamp(d, -7 * dt, 7 * dt);
  }

  _springs(dt) {
    this.recoilPitch.step(dt);
    this.recoilYaw.step(dt);
    this.recoilBack.step(dt);
    for (const k in this.flinch) this.flinch[k].s.step(dt);
    this.breath += dt * (this.speed > 3 ? 2.4 : 1.15);
    this.lean += (this.leanTarget - this.lean) * Math.min(1, dt * 7);
    this.postureBlend += (this.postureTarget - this.postureBlend) * Math.min(1, dt * 6);
    this.crouchAmt += ((this.crouched ? 1 : 0) - this.crouchAmt) * Math.min(1, dt * 8);
    if (this.action) {
      this.action.t += dt;
      if (this.action.t >= this.action.dur) this.action = null;
    }
  }

  /* ---------------------------------------------------------------- *
   * gait
   * ---------------------------------------------------------------- */
  /**
   * How far the ankle of a locked foot can travel, forward of and behind the hip,
   * without the hips having to drop out from under the character.
   *
   * `front` is measured at heel strike (hips at the top of the bob, ankle just off the
   * sole); `back` is measured at toe-off (hips at the bottom of the bob, plus the one
   * authored dip we allow, and the ankle raised and carried forward by the toe pivot).
   * Both are pure scalar geometry — no rays, no allocation — so this can run per frame.
   */
  /** Longest hip→ankle distance the leg may be asked for before the hips must drop. */
  _maxLeg() { return (this.legLen - 0.012 * this.S) * 0.998; }

  _reach() {
    const R = this.rig, S = this.S, B = this.gaitBlend;
    const maxLen = this._maxLeg();
    const hip0 = R.bind.thighL.y - (SOFT_KNEE + B.sink + this.crouchAmt * 0.30) * S;
    const bobAmp = B.bob * S;

    const vFront = Math.max(0.05, hip0 - this.ankleH);
    this.reachFront = Math.sqrt(Math.max(0, maxLen * maxLen - vFront * vFront));

    // toe pivot at peak roll: ankle rises `liftUp` and travels `liftFwd` forward while
    // the toe stays exactly where it was put down
    const th = B.toeOff;
    const c = Math.cos(th), sn = Math.sin(th);
    this.liftUp = this.toeFwd * sn - this.toeDrop * (1 - c);
    this.liftFwd = this.toeFwd * (1 - c) + this.toeDrop * sn;

    const vBack = Math.max(0.05, hip0 - bobAmp - HIP_GIVE * S - this.ankleH - this.liftUp);
    this.reachBack = Math.sqrt(Math.max(0, maxLen * maxLen - vBack * vBack)) + this.liftFwd;
  }

  _gait(dt) {
    const s = this.speed;
    let name, amt;
    if (this.crouched) { name = 'crouch'; amt = s < 0.12 ? 0 : clamp(s / 1.2, 0.3, 1); }
    else if (s < 0.12) { name = 'idle'; amt = 0; }
    else if (s < 1.9) { name = 'walk'; amt = clamp(s / 1.9, 0.3, 1); }
    else if (s < 4.0) { name = 'run'; amt = 1; }
    else { name = 'sprint'; amt = 1; }
    this.gaitName = name;

    const g = GAITS[name], idle = GAITS.idle, B = this.gaitBlend;
    const k = Math.min(1, dt * 6);
    for (const key in g) B[key] += (lerp(idle[key], g[key], amt) - B[key]) * k;

    /* ---- reconcile the authored gait with what the leg can physically do ---- */
    this._reach();
    const strideM = Math.max(1e-4, B.stride * this.S);
    // The plant lands at a fraction of the reachable front budget, so it is always
    // under or just ahead of the hip — never a stride-length out in front.
    this.aheadM = Math.min(B.ahead * this.reachFront, AHEAD_OF_STRIDE * strideM);
    // A foot may only stay locked for as long as the leg can cover the ground the body
    // travels underneath it. Anything longer and the hips get dragged down: the lunge.
    const dutyMax = (this.aheadM + this.reachBack) / (2 * strideM);
    this.duty = clamp(Math.min(B.duty, dutyMax), 0.10, 1);

    // Turn-in-place: no translation, but a fast pivot still has to re-plant the feet
    // or the soles grind. Feed the yaw rate in as if it were a small sideways step.
    const turn = Math.abs(this.yawRate) * 0.30 * this.S;
    const travel = (this.speed + (this.speed < 0.5 ? turn : 0)) * dt;

    if (B.stride > 0.03 && travel > 1e-5) {
      this.phase = (this.phase + travel / (2 * B.stride * this.S)) % 1;
    } else {
      this.phase = (this.phase + dt * 0.05) % 1;   // idle weight-shift drift
    }

    const rp = this.root.position;
    const hit = this._ray(_v1.set(rp.x, rp.y + 1.4 * this.S, rp.z), DOWN, 4 * this.S);
    if (hit) { this.groundY = hit.point.y; this.groundNormal.copy(hit.normal); }
    else { this.groundY = rp.y; this.groundNormal.set(0, 1, 0); }

    this._stepFoot(this.feet.L, dt);
    this._stepFoot(this.feet.R, dt);
  }

  _ray(o, d, far) {
    const P = this.physics;
    if (!P || !P.raycast) return null;
    try { return P.raycast(o, d, far, P.MASK ? P.MASK.WORLD : 1); } catch (err) { return null; }
  }

  _stepFoot(f, dt) {
    const B = this.gaitBlend;
    const S = this.S;
    const p = (this.phase + f.offset) % 1;
    const duty = this.duty;

    if (B.stride < 0.03) {
      const n = this._neutral(f, this._t[0]);
      if (!f.plantValid || n.distanceTo(f.plant) > 0.26 * S
          || Math.abs(f.plantYaw - this.yaw) > 0.45) {
        this._plantFoot(f, n);
      }
      f.target.copy(f.plant);
      f.roll += (0 - f.roll) * Math.min(1, dt * 8);
      f.planted = true;
      f.ground = 1;
      return;
    }

    if (p < duty) {
      if (!f.planted) this._plantFoot(f, this._predict(f, this._t[0]));
      f.planted = true;
      f.target.copy(f.plant);
      const u = p / duty;
      // heel strike (toe up), flat, then a heel-off roll (toe down). `_legIK` turns the
      // positive half of this into a pivot about the toe, so the sole never slides.
      f.roll = -0.26 * B.toeOff * 1.6 * (1 - smooth(0, 0.18, u)) + B.toeOff * smooth(0.52, 1.0, u);
      f.ground = 1;
    } else {
      if (f.planted) {
        f.lift.copy(f.plant);
        // Remember lift-off as an offset from the BODY, not as a world point.
        f.liftOff.copy(f.plant).sub(this.root.position);
        f.planted = false; f.swingValid = false;
      }
      // Re-predict every frame and chase it: the body keeps moving during the swing, so
      // a plant predicted once at lift-off is stale by the time the foot lands, and a
      // stale plant is a foot that skates on touchdown.
      const want = this._predict(f, this._t[0]);
      if (!f.swingValid) { f.swingTo.copy(want); f.swingValid = true; }
      else f.swingTo.lerp(want, Math.min(1, dt * 14));
      const u = clamp((p - duty) / (1 - duty), 0, 1);
      const e = u * u * (3 - 2 * u);
      /**
       * The swing interpolates HORIZONTALLY IN THE BODY'S FRAME. A world-space lerp
       * between the lift-off point and the predicted plant leaves the foot standing
       * still in the world while the body runs off it: at a 0.21 duty the body covers
       * 2.4 m during one flight phase, and the measured swing ankle ended up **1.10 m
       * behind the hip** — 27 cm past what the leg can reach. The hips were then
       * dragged 34 cm down every cycle trying to reach it. That, not the plant offset,
       * is where most of the lunge came from.
       *
       * Vertically it stays in world space, because the ground does not move with the
       * body and the foot has to arrive at the real surface height.
       */
      const rp = this.root.position;
      f.target.set(
        lerp(rp.x + f.liftOff.x, f.swingTo.x, e),
        lerp(f.lift.y, f.swingTo.y, e),
        lerp(rp.z + f.liftOff.z, f.swingTo.z, e),
      );
      f.target.y += Math.sin(Math.PI * u) * B.lift * S;
      f.roll = lerp(B.toeOff * 0.85, -0.30, e);
      // Fade the toe pivot out over the first quarter of the swing instead of
      // dropping it the instant the foot leaves the ground — the ankle is ~6 cm
      // forward of the plant at toe-off and cutting that in one frame is a pop.
      f.ground = 1 - smooth(0, 0.25, u);
    }
  }

  _neutral(f, out) {
    return out.set(0.104 * this.S * f.side, 0, 0)
      .applyQuaternion(this._rootQ).add(this.root.position);
  }

  /**
   * Where the next plant should be. `aheadM` is a fraction of the leg's reachable
   * front budget (see `_reach`), so this lands under or just ahead of the hip at any
   * speed instead of a stride-length out in front of it.
   */
  _predict(f, out) {
    const S = this.S;
    this._neutral(f, out);
    if (this.speed > 0.05) {
      _v2.copy(this.velocity).normalize().multiplyScalar(this.aheadM);
      out.add(_v2);
    }
    if (Math.abs(this.yawRate) > 0.5 && this.speed < 0.6) {
      _v2.set(0, 0, f.side * 0.09 * S * Math.sign(this.yawRate)).applyQuaternion(this._rootQ);
      out.add(_v2);
    }
    const hit = this._ray(_v2.set(out.x, out.y + 0.95 * S, out.z), DOWN, 2.8 * S);
    if (hit) { out.y = hit.point.y; f.pendingNormal.copy(hit.normal); f.pendingValid = true; }
    else { out.y = this.groundY; f.pendingNormal.set(0, 1, 0); f.pendingValid = true; }
    return out;
  }

  _plantFoot(f, pos) {
    f.plant.copy(pos);
    f.plantValid = true;
    f.plantYaw = this.yaw;
    f.swingValid = false;
    if (f.pendingValid) { f.normal.copy(f.pendingNormal); f.pendingValid = false; return; }
    const hit = this._ray(_v2.set(pos.x, pos.y + 0.7 * this.S, pos.z), DOWN, 1.8 * this.S);
    if (hit) { f.plant.y = hit.point.y; f.normal.copy(hit.normal); }
    else f.normal.set(0, 1, 0);
  }

  /* ---------------------------------------------------------------- *
   * layer 0 + 3 — base FK pose with the additive springs folded in
   * ---------------------------------------------------------------- */
  _basePose() {
    const R = this.rig;
    const B = this.gaitBlend;
    const S = this.S;
    const ph = this.phase * Math.PI * 2;

    for (const n of R.names) {
      if (DYNAMIC_BONES.indexOf(n) >= 0) continue;
      R.byName.get(n).quaternion.identity();
    }

    const bob = -Math.abs(Math.sin(ph)) * B.bob * S;
    const sway = Math.sin(ph) * B.stride * 0.045 * S;
    const idleShift = Math.sin(this.time * 0.62) * 0.012 * S * (1 - Math.min(1, this.speed));
    const idleY = Math.sin(this.time * 0.31) * 0.006 * S * (1 - Math.min(1, this.speed));
    const breatheY = Math.sin(this.breath * 1.9) * 0.005 * S * (1 - Math.min(1, this.speed * 0.5));

    const hips = R.byName.get('hips');
    hips.position.set(
      R.bind.hips.x + sway + idleShift,
      R.bind.hips.y + bob + breatheY + idleY
        - (SOFT_KNEE + B.sink + this.crouchAmt * 0.30) * S,
      R.bind.hips.z - this.crouchAmt * 0.05 * S,
    );

    const pelvisRoll = Math.sin(ph) * B.stride * 0.20;
    const pelvisYaw = -Math.sin(ph) * B.stride * 0.16;
    _e.set(this.crouchAmt * 0.12, pelvisYaw, pelvisRoll, 'YXZ');
    hips.quaternion.setFromEuler(_e);

    const leanF = B.lean + this.crouchAmt * 0.22;
    const counter = -pelvisYaw * 0.6;
    const breath = Math.sin(this.breath * 1.9) * 0.018 * (1 - Math.min(1, this.speed * 0.4));
    const setE = (n, x, yy, z) => { _e.set(x, yy, z, 'YXZ'); R.byName.get(n).quaternion.setFromEuler(_e); };

    setE('spine01', leanF * 0.32 + breath * 0.30, counter * 0.30, this.lean * 0.10 - pelvisRoll * 0.40);
    setE('spine02', leanF * 0.34 + breath * 0.40, counter * 0.35, this.lean * 0.14 - pelvisRoll * 0.30);
    setE('chest', leanF * 0.30 + breath * 0.30, counter * 0.35, this.lean * 0.16 - pelvisRoll * 0.18);
    setE('neck', -leanF * 0.40, 0, -this.lean * 0.12);
    setE('head', -leanF * 0.28, 0, -this.lean * 0.10);

    // BLADED STANCE: a right-handed shooter turns the left shoulder toward the target.
    // Without this the support arm has to reach further than it physically can and the
    // IK straightens it — the single loudest "this rig is faking it" tell.
    const blade = 0.22 * this.postureBlend;
    this._add('spine01', 0, blade * 0.20, 0);
    this._add('spine02', 0, blade * 0.35, 0);
    this._add('chest', 0, blade * 0.45, 0);
    this._add('neck', 0, -blade * 0.35, 0);

    const sw = Math.sin(ph) * B.armSwing;
    setE('clavicleL', 0, 0, -0.05 - breath * 0.5 - Math.max(0, sw) * 0.10);
    setE('clavicleR', 0, 0, 0.05 + breath * 0.5 + Math.max(0, -sw) * 0.10);
    setE('upperArmL', sw * 0.9, 0, 0.11);
    setE('forearmL', -0.30 - Math.max(0, sw) * 0.55, 0, 0);
    setE('upperArmR', -sw * 0.9, 0, -0.11);
    setE('forearmR', -0.30 - Math.max(0, -sw) * 0.55, 0, 0);

    // --- additive: recoil ---
    const rp = this.recoilPitch.x, ry = this.recoilYaw.x;
    this._add('chest', rp * 0.010, ry * 0.008, 0);
    this._add('spine02', rp * 0.005, ry * 0.004, 0);
    this._add('neck', rp * 0.006, ry * 0.006, 0);

    // --- additive: per-region hit flinch ---
    for (const region in this.flinch) {
      const f = this.flinch[region];
      const a = f.s.x;
      if (Math.abs(a) < 1e-4) continue;
      _v1.copy(f.dir).applyQuaternion(_qa.copy(this._rootQ).invert());
      const px = a * 0.018 * -_v1.z;
      const pz = a * 0.018 * _v1.x;
      switch (region) {
        case 'head': case 'neck':
          this._add('head', px * 1.6, 0, pz * 1.6); this._add('neck', px * 0.9, 0, pz * 0.9); break;
        case 'armL': case 'handL': this._add('upperArmL', px * 2.4, 0, pz * 2.4); break;
        case 'armR': case 'handR': this._add('upperArmR', px * 2.4, 0, pz * 2.4); break;
        case 'legL': case 'footL': this._add('thighL', px * 1.5, 0, pz * 1.5); break;
        case 'legR': case 'footR': this._add('thighR', px * 1.5, 0, pz * 1.5); break;
        default:
          this._add('chest', px * 1.0, 0, pz * 1.0);
          this._add('spine02', px * 0.7, 0, pz * 0.7);
          this._add('spine01', px * 0.5, 0, pz * 0.5);
          this._add('neck', px * 0.5, 0, pz * 0.5);
      }
    }
  }

  _add(name, x, y, z) {
    const b = this.rig.byName.get(name);
    if (!b) return;
    _e.set(x, y, z, 'YXZ');
    b.quaternion.multiply(_qa.setFromEuler(_e));
  }

  /* ---------------------------------------------------------------- *
   * layer 1 — spine aim
   * ---------------------------------------------------------------- */
  _spineAim(dt) {
    if (!this.hasAim) return;
    this.aimSmooth.lerp(this.aimTarget, Math.min(1, dt * 9));

    this.rig.byName.get('chest').getWorldPosition(_v1);
    _v2.copy(this.aimSmooth).sub(_v1);
    const yawTo = Math.atan2(_v2.x, _v2.z);
    const pitchTo = Math.atan2(_v2.y, Math.hypot(_v2.x, _v2.z));

    let dYaw = yawTo - this.yaw;
    while (dYaw > Math.PI) dYaw -= Math.PI * 2;
    while (dYaw < -Math.PI) dYaw += Math.PI * 2;
    dYaw = clamp(dYaw, -1.3, 1.3);
    const dPitch = clamp(pitchTo, -0.8, 0.8);
    const w = this.aimWeight * (1 - this.crouchAmt * 0.15);

    // per-bone limits — the lumbar barely twists, the chest carries most of it
    const share = [['spine01', 0.14, 0.10], ['spine02', 0.30, 0.20], ['chest', 0.42, 0.34]];
    for (const [name, ys, ps] of share) {
      _e.set(-dPitch * ps * w, dYaw * ys * w, 0, 'YXZ');
      this.rig.byName.get(name).quaternion.multiply(_qa.setFromEuler(_e));
    }
  }

  /* ---------------------------------------------------------------- *
   * layer 4a — legs
   * ---------------------------------------------------------------- */
  _legIK() {
    const R = this.rig;
    const S = this.S;
    const mid = this._t[1], end = this._t[2];

    /**
     * The IK target is the ANKLE, not the sole. `f.target` is where the FLAT foot's
     * ankle would sit; the roll then pivots the foot about whichever end is actually
     * touching, and the ankle is moved to match:
     *
     *   roll > 0  heel-off, pivot on the TOE — ankle rises AND travels forward, which
     *             is the whole back half of the stride's reach,
     *   roll < 0  heel strike, pivot on the HEEL — ankle sits back and slightly up,
     *             instead of the heel being driven through the ground.
     *
     * Because the pivot is the contact point, the sole's footprint does not move: this
     * is what makes stance-phase foot slip zero by construction rather than by tuning.
     */
    for (const side of ['L', 'R']) {
      const f = this.feet[side];
      const ankleH = R.bind['foot' + side].y;
      if (!f.ankle) f.ankle = new THREE.Vector3();
      f.ankle.copy(f.target).addScaledVector(f.normal, ankleH * 0.5).addScaledVector(UP, ankleH * 0.5);

      const g = f.ground ?? 1;
      const th = f.roll;
      if (g > 1e-3 && Math.abs(th) > 1e-3) {
        const c = Math.cos(th), sn = Math.sin(th);
        let fw, up;
        if (th > 0) {                                   // toe pivot
          fw = this.toeFwd * (1 - c) + this.toeDrop * sn;
          up = this.toeFwd * sn - this.toeDrop * (1 - c);
        } else {                                        // heel pivot
          fw = this.heelBack * (c - 1) + ankleH * sn;
          up = ankleH * (c - 1) - this.heelBack * sn;
        }
        _qa.setFromAxisAngle(UP, f.plantYaw);
        _v1.copy(FWD).applyQuaternion(_qa);
        _v1.addScaledVector(f.normal, -_v1.dot(f.normal));
        if (_v1.lengthSq() > 1e-8) {
          _v1.normalize();
          f.ankle.addScaledVector(_v1, fw * g).addScaledVector(f.normal, up * g);
        }
      }
    }

    // Hip height: drop until BOTH legs reach their ankle target. Stairs and slopes need
    // nothing else — no per-surface special case anywhere in this file.
    //
    // On flat ground this should now read ~0: the gait's own duty clamp keeps the plant
    // inside the leg's reach, so the drop is a stairs/slope safety valve rather than the
    // thing that was quietly crouching every running soldier by 7.5 cm.
    let drop = 0;
    const max = this._maxLeg();
    for (const side of ['L', 'R']) {
      R.byName.get('thigh' + side).getWorldPosition(_v1);
      const d = _v1.distanceTo(this.feet[side].ankle);
      if (d > max) drop = Math.max(drop, d - max);
    }
    // Frame-rate independent: the old per-frame 0.45 converged in three frames at
    // 120 Hz and in one at 30, and the lag while it caught up is foot slip.
    this.hipDrop += (Math.min(drop, 0.34 * S) - this.hipDrop)
      * (1 - Math.exp(-Math.max(1e-4, this.dt) * 55));
    if (this.hipDrop > 1e-4) {
      R.byName.get('hips').position.y -= this.hipDrop;
      R.rootBone.updateMatrixWorld(true);
    }

    const fwd = this._t[3].copy(FWD).applyQuaternion(this._rootQ);
    const right = this._t[4].copy(RIGHT).applyQuaternion(this._rootQ);

    for (const side of ['L', 'R']) {
      const f = this.feet[side];
      const thigh = R.byName.get('thigh' + side);
      const shin = R.byName.get('shin' + side);
      const foot = R.byName.get('foot' + side);

      const ankle = this._t[5].copy(f.ankle);
      const pole = this._t[6].copy(fwd).addScaledVector(right, f.side * 0.20).normalize();

      thigh.getWorldPosition(_v1);
      twoBoneIK(_v1, ankle, this.thighLen, this.shinLen, pole, mid, end);
      this._aimBone(thigh, 'thigh' + side, 'shin' + side, this._t[7].copy(mid).sub(_v1), pole, FWD);
      R.rootBone.updateMatrixWorld(true);

      shin.getWorldPosition(_v1);
      this._aimBone(shin, 'shin' + side, 'foot' + side, this._t[7].copy(end).sub(_v1), pole, FWD);
      R.rootBone.updateMatrixWorld(true);

      // foot: sole flat on the surface, yaw locked to the plant, stride roll on top
      _qa.setFromAxisAngle(UP, f.plantYaw);
      const ffwd = this._t[8].copy(FWD).applyQuaternion(_qa);
      ffwd.addScaledVector(f.normal, -ffwd.dot(f.normal));
      if (ffwd.lengthSq() < 1e-8) ffwd.copy(fwd);
      ffwd.normalize();
      basisQuat(f.normal, ffwd, _qb);
      _qb.multiply(_qc.setFromAxisAngle(RIGHT, f.roll));
      foot.parent.getWorldQuaternion(_qd);
      foot.quaternion.copy(_qd.invert()).multiply(_qb);
    }
    R.rootBone.updateMatrixWorld(true);
  }

  /**
   * Rotate `bone` so its bind direction (from -> to) points along `dirWorld`, with
   * `refWorld` fixing the roll against the bind-space reference `refBind`.
   * Bind rotations are identity, so the bind frame is simply the root's frame.
   */
  _aimBone(bone, from, to, dirWorld, refWorld, refBind) {
    const R = this.rig;
    _v1.copy(R.bind[to]).sub(R.bind[from]).normalize().applyQuaternion(this._rootQ);
    _v2.copy(refBind).applyQuaternion(this._rootQ);
    basisQuat(_v1, _v2, _qa);
    basisQuat(dirWorld, refWorld, _qb);
    _qb.multiply(_qa.invert());        // world-space delta from bind
    _qb.multiply(this._rootQ);         // -> desired bone world quaternion
    bone.parent.getWorldQuaternion(_qc);
    bone.quaternion.copy(_qc.invert()).multiply(_qb);
  }

  /* ---------------------------------------------------------------- *
   * layer 2 + 4b — the weapon anchor and the arms that hold it
   * ---------------------------------------------------------------- */
  _weaponAnchor(out) {
    const S = this.S;
    const posture = this.postureBlend;
    // Where the PISTOL GRIP sits in character space. Shouldered puts it forward of and
    // below the right shoulder so the elbow lands at ~110°, which is the difference
    // between a soldier and a man hugging a rifle.
    let x = lerp(0.088, 0.062, posture);
    let y = lerp(1.185, 1.352, posture);
    let z = lerp(0.205, 0.272, posture);

    if (this.gaitName === 'sprint') { x = 0.055; y = 1.115; z = 0.235; }
    y -= this.crouchAmt * 0.26;

    const A = this.action;
    if (A) {
      const u = clamp(A.t / A.dur, 0, 1);
      if (A.name === 'reload' || A.name === 'reloadTactical') {
        const k = Math.sin(Math.PI * clamp(u * 1.12, 0, 1));
        x -= 0.05 * k; y -= 0.13 * k; z -= 0.02 * k;
      } else if (A.name === 'vault') {
        const k = Math.sin(Math.PI * u);
        x -= 0.10 * k; y -= 0.24 * k; z += 0.05 * k;
      } else if (A.name === 'throw') {
        const k = Math.sin(Math.PI * clamp(u * 1.2, 0, 1));
        x += 0.03 * k; y -= 0.16 * k;
      } else if (A.name === 'melee') {
        const k = Math.sin(Math.PI * u);
        y += 0.07 * k; z += 0.17 * k;
      }
    }

    x += this.lean * 0.06;
    z += this.recoilBack.x * 0.013;

    out.set(x * S, y * S, z * S).applyQuaternion(this._rootQ).add(this.root.position);
    const hips = this.rig.byName.get('hips');
    out.y += (hips.position.y - this.rig.bind.hips.y) * 0.6;
    return out;
  }

  _armIK() {
    const R = this.rig;
    const S = this.S;
    const G = this.gear;
    if (!G.weaponGrip) return;
    const T = this._t;
    const mid = T[1], end = T[2];

    const anchor = this._weaponAnchor(T[0]);

    // ---- aim direction ----
    const dir = T[3];
    if (this.hasAim) dir.copy(this.aimSmooth).sub(anchor).normalize();
    else dir.copy(FWD).applyQuaternion(this._rootQ);

    const rp = this.recoilPitch.x * 0.013, ry = this.recoilYaw.x * 0.011;
    dir.applyQuaternion(_qa.setFromAxisAngle(UP, ry));
    _bx.crossVectors(dir, UP);
    if (_bx.lengthSq() > 1e-8) dir.applyQuaternion(_qa.setFromAxisAngle(_bx.normalize(), rp));

    if (this.gaitName === 'sprint') {
      dir.set(0.34, -0.52, 0.78).normalize().applyQuaternion(this._rootQ);
    }
    const A = this.action;
    if (A && (A.name === 'reload' || A.name === 'reloadTactical')) {
      const k = Math.sin(Math.PI * clamp(A.t / A.dur * 1.12, 0, 1));
      T[4].set(0.40, -0.34, 0.85).normalize().applyQuaternion(this._rootQ);
      dir.lerp(T[4], k * 0.8).normalize();
    } else if (A && A.name === 'vault') {
      const k = Math.sin(Math.PI * clamp(A.t / A.dur, 0, 1));
      T[4].set(0.30, -0.62, 0.72).normalize().applyQuaternion(this._rootQ);
      dir.lerp(T[4], k * 0.9).normalize();
    }

    // cant: sight stays upright, plus a little roll while moving and while leaning
    const up = T[5].copy(UP);
    const cant = Math.sin(this.phase * Math.PI * 2) * this.gaitBlend.armSwing * 0.09 + this.lean * 0.20;
    up.applyQuaternion(_qa.setFromAxisAngle(dir, cant));
    frameQuatZ(dir, up, this.weaponQuat);

    // ---- right hand puts the grip on the anchor ----
    const gripLocal = T[6].copy(G.weaponGrip).sub(R.bind.handR);
    const handPos = T[7].copy(gripLocal).applyQuaternion(this.weaponQuat).multiplyScalar(-1).add(anchor);
    this.weaponPos.copy(anchor);

    // elbow out to the RIGHT and down — the modern tucked firing elbow
    const armPoleR = T[8].set(0.52, -0.76, -0.38).normalize().applyQuaternion(this._rootQ);
    const shoulderR = R.byName.get('upperArmR');
    shoulderR.getWorldPosition(_v1);
    twoBoneIK(_v1, handPos, this.upperArmLen, this.forearmLen, armPoleR, mid, end);
    this._aimBone(shoulderR, 'upperArmR', 'forearmR', T[9].copy(mid).sub(_v1), armPoleR, BACK);
    R.rootBone.updateMatrixWorld(true);
    const foreR = R.byName.get('forearmR');
    foreR.getWorldPosition(_v1);
    this._aimBone(foreR, 'forearmR', 'handR', T[9].copy(end).sub(_v1), armPoleR, BACK);
    R.rootBone.updateMatrixWorld(true);

    const handR = R.byName.get('handR');
    handR.parent.getWorldQuaternion(_qc);
    handR.quaternion.copy(_qc.invert()).multiply(this.weaponQuat);
    R.rootBone.updateMatrixWorld(true);

    // ---- left hand onto the foregrip, wherever the weapon actually ended up ----
    handR.getWorldPosition(_v1);
    handR.getWorldQuaternion(_qb);
    const lgLocal = T[6].copy(G.weaponLeftGrip).sub(R.bind.handR);
    const foreGrip = T[7].copy(lgLocal).applyQuaternion(_qb).add(_v1);
    const leftTarget = T[8].copy(foreGrip);

    if (A && (A.name === 'reload' || A.name === 'reloadTactical')) {
      const u = clamp(A.t / A.dur, 0, 1);
      const pouch = _v2.set(-0.09 * S, 1.15 * S, 0.19 * S).applyQuaternion(this._rootQ).add(this.root.position);
      const well = _v3.copy(lgLocal).setZ(lgLocal.z * 0.30).applyQuaternion(_qb).add(_v1);
      if (u < 0.30) leftTarget.lerp(pouch, smooth(0, 0.30, u));
      else if (u < 0.55) leftTarget.copy(pouch).lerp(well, smooth(0.30, 0.55, u));
      else if (u < 0.78) leftTarget.copy(well);
      else leftTarget.copy(well).lerp(foreGrip, smooth(0.78, 1.0, u));
    } else if (A && A.name === 'throw') {
      const u = clamp(A.t / A.dur, 0, 1);
      const back = _v2.set(-0.32 * S, 1.58 * S, -0.26 * S).applyQuaternion(this._rootQ).add(this.root.position);
      const fwdP = _v3.set(-0.08 * S, 1.74 * S, 0.48 * S).applyQuaternion(this._rootQ).add(this.root.position);
      if (u < 0.45) leftTarget.lerp(back, smooth(0, 0.45, u));
      else if (u < 0.62) leftTarget.copy(back).lerp(fwdP, smooth(0.45, 0.62, u));
      else leftTarget.copy(fwdP).lerp(foreGrip, smooth(0.62, 1.0, u));
    } else if (A && A.name === 'vault') {
      const u = clamp(A.t / A.dur, 0, 1);
      const ledge = _v2.set(-0.20 * S, 1.10 * S, 0.52 * S).applyQuaternion(this._rootQ).add(this.root.position);
      leftTarget.lerp(ledge, Math.sin(Math.PI * u) * 0.9);
    } else if (A && A.name === 'point') {
      // squad hand signal: left arm out toward the aim, held, then back on the gun
      const u = clamp(A.t / A.dur, 0, 1);
      const k = smooth(0, 0.22, u) * (1 - smooth(0.72, 1.0, u));
      _v2.copy(this.hasAim ? this.aimSmooth : foreGrip).sub(this.root.position);
      _v2.y = 0;
      if (_v2.lengthSq() < 1e-6) _v2.copy(FWD).applyQuaternion(this._rootQ);
      _v2.normalize().multiplyScalar(0.62 * S);
      _v3.copy(this.root.position).add(_v2);
      _v3.y = this.root.position.y + 1.52 * S;
      leftTarget.lerp(_v3, k * 0.95);
    }

    // support elbow tucks down and slightly inboard, under the handguard
    const armPoleL = T[9].set(-0.26, -0.93, -0.26).normalize().applyQuaternion(this._rootQ);
    const shoulderL = R.byName.get('upperArmL');
    shoulderL.getWorldPosition(_v1);
    twoBoneIK(_v1, leftTarget, this.upperArmLen, this.forearmLen, armPoleL, mid, end);
    this._aimBone(shoulderL, 'upperArmL', 'forearmL', _v3.copy(mid).sub(_v1), armPoleL, BACK);
    R.rootBone.updateMatrixWorld(true);
    const foreL = R.byName.get('forearmL');
    foreL.getWorldPosition(_v1);
    this._aimBone(foreL, 'forearmL', 'handL', _v3.copy(end).sub(_v1), armPoleL, BACK);
    R.rootBone.updateMatrixWorld(true);

    const handL = R.byName.get('handL');
    handL.parent.getWorldQuaternion(_qc);
    _e.set(0.5, 0, 1.25, 'YXZ');
    _qd.copy(this.weaponQuat).multiply(_qa.setFromEuler(_e));
    handL.quaternion.copy(_qc.invert()).multiply(_qd);

    // forearm twist helpers carry half the wrist roll — this is what stops the sleeve
    // from candy-wrappering when the hand rolls onto a handguard
    for (const s of ['L', 'R']) {
      const h = R.byName.get('hand' + s);
      const tw = R.byName.get('fTwist' + s);
      _v1.copy(R.bind['hand' + s]).sub(R.bind['forearm' + s]).normalize();
      tw.quaternion.setFromAxisAngle(_v1, twistAngle(h.quaternion, _v1) * 0.5);
    }
    R.rootBone.updateMatrixWorld(true);
  }

  /* ---------------------------------------------------------------- *
   * layer 5 — look-at
   * ---------------------------------------------------------------- */
  _lookAt(dt) {
    const R = this.rig;
    const target = this.hasLook ? this.lookTarget : (this.hasAim ? this.aimSmooth : null);
    if (!target) return;
    this.lookSmooth.lerp(target, Math.min(1, dt * 5.5));   // lead-lag

    const neck = R.byName.get('neck');
    const head = R.byName.get('head');
    neck.getWorldPosition(_v1);
    neck.parent.getWorldQuaternion(_qa);
    _v2.copy(this.lookSmooth).sub(_v1).applyQuaternion(_qa.invert()).normalize();

    const yaw = clamp(Math.atan2(_v2.x, _v2.z), -1.25, 1.25);
    const pitch = clamp(-Math.atan2(_v2.y, Math.hypot(_v2.x, _v2.z)), -0.60, 0.50);
    const k = Math.min(1, dt * 7);
    this.headYaw += (yaw - this.headYaw) * k;
    this.headPitch += (pitch - this.headPitch) * k;

    _e.set(this.headPitch * 0.40, this.headYaw * 0.40, 0, 'YXZ');
    neck.quaternion.multiply(_qb.setFromEuler(_e));
    _e.set(this.headPitch * 0.60, this.headYaw * 0.60, 0, 'YXZ');
    head.quaternion.multiply(_qb.setFromEuler(_e));
  }

  /* ---------------------------------------------------------------- *
   * ragdoll handoff
   * ---------------------------------------------------------------- */
  attachRagdoll(ragdoll, driveTable) {
    this.ragdoll = ragdoll;
    this.driveTable = driveTable;
    this.dead = true;
    this.ragdollBlend = 0;
  }

  /**
   * Read the ragdoll capsule's world axis for one drive entry.
   *
   * Preferred source is the two particle positions, because `bone.quaternion` is not a
   * unit quaternion (see the note on RAGDOLL_DRIVE). The quaternion path is kept as a
   * fallback for a ragdoll implementation that does not expose `jointPosition`, and it
   * is normalised on the way out so it can never smuggle a scale into the skeleton.
   */
  _ragdollAxis(d, rd, out) {
    if (typeof rd.jointPosition === 'function' && d.jf && d.jt) {
      const a = rd.jointPosition(d.jf, _v2);
      const b = rd.jointPosition(d.jt, out);
      if (a && b) {
        out.sub(a);
        if (out.lengthSq() > 1e-8) return out.normalize();
      }
    }
    const rb = rd.bone(d.rb);
    if (!rb) return null;
    out.copy(UP).applyQuaternion(rb.quaternion);
    return out.lengthSq() > 1e-8 ? out.normalize() : null;
  }

  _applyRagdoll() {
    const R = this.rig;
    const rd = this.ragdoll;
    const blend = this.ragdollBlend;

    // Hip joint straight off the particle when we can — the capsule centre plus a
    // half-length along a non-unit quaternion lands short.
    let hip = null;
    if (typeof rd.jointPosition === 'function') hip = rd.jointPosition('hip', _v1);
    if (!hip) {
      const pel = rd.bone('pelvis');
      if (pel) {
        hip = _v1.copy(pel.position)
          .addScaledVector(_v2.set(0, -1, 0).applyQuaternion(pel.quaternion).normalize(), pel.length * 0.5);
      }
    }
    if (hip) {
      this.root.worldToLocal(hip);
      R.byName.get('hips').position.lerp(hip, blend);
      R.rootBone.updateMatrixWorld(true);
    }

    this.root.getWorldQuaternion(this._rootQ);
    for (const d of this.driveTable) {
      const axis = this._ragdollAxis(d, rd, _v3);
      if (!axis) continue;
      // bind direction, carried into world by the character's own facing
      _v1.copy(R.bind[d.to]).sub(R.bind[d.from]).normalize().applyQuaternion(this._rootQ);
      // minimal rotation from the bind direction onto the ragdoll's limb axis, then
      // back through the root so the bone keeps the body's facing as its roll reference
      _qa.setFromUnitVectors(_v1, axis);
      _qb.copy(_qa).multiply(this._rootQ).normalize();
      for (const name of d.bones) {
        const bone = R.byName.get(name);
        if (!bone) continue;
        bone.parent.getWorldQuaternion(_qc);
        _qc.invert().multiply(_qb).normalize();
        bone.quaternion.slerp(_qc, blend).normalize();
        R.rootBone.updateMatrixWorld(true);
      }
    }
    R.rootBone.updateMatrixWorld(true);
  }
}

const BACK = new THREE.Vector3(0, -0.15, -1).normalize();

/** Signed rotation of `q` about `axis` (swing–twist decomposition). */
function twistAngle(q, axis) {
  const d = axis.x * q.x + axis.y * q.y + axis.z * q.z;
  let a = 2 * Math.atan2(d, q.w);
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}
